import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";
import {
  DIALOG_DESCRIPTION_CLASSNAME,
  DIALOG_OVERLAY_CLASSNAME,
  DIALOG_TITLE_CLASSNAME,
  DIALOG_TITLE_VARIANTS,
  DialogContentLayoutContext,
  type DialogTitleSize,
  OverlayOpenContext,
  getDialogContentClassName,
  getDialogFooterClassName,
  getDialogHeaderClassName,
  getZIndexClass,
  useControllableOpen,
  useFrozenWhileClosed,
} from "@src/lib/ui/dialogShell";

const noRegistration = (): (() => void) => () => undefined;

/**
 * Radix `AlertDialog.Root`, tracking its open state so the content can keep
 * its last open render on screen while the close animation runs.
 */
const AlertDialog = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Root
>): React.JSX.Element => {
  const [open, setOpen] = useControllableOpen({
    open: openProp,
    defaultOpen,
    onOpenChange,
  });
  return (
    <OverlayOpenContext.Provider value={open}>
      <AlertDialogPrimitive.Root
        open={open}
        onOpenChange={setOpen}
        {...props}
      />
    </OverlayOpenContext.Provider>
  );
};
AlertDialog.displayName = "AlertDialog";

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(DIALOG_OVERLAY_CLASSNAME, className)}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

/**
 * The same panel as `DialogContent`, without a close button: an alert dialog
 * has to be answered through its Cancel or Action.
 */
const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const { className: contentClassName, isContainer } =
    getDialogContentClassName(className);
  // No close button, so no title-size or description registration; Radix's default
  // aria-describedby stays in place.
  const layout = React.useMemo(
    () => ({
      isContainer,
      hasCloseButton: false,
      registerDescription: noRegistration,
      reportTitleSize: noRegistration,
    }),
    [isContainer],
  );

  // Keep the last open render on screen while the close animation runs.
  // A force-mounted panel stays mounted while closed, so it stays live.
  const frozenChildren = useFrozenWhileClosed(children);
  const renderedChildren = props.forceMount ? children : frozenChildren;

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay className={getZIndexClass(contentClassName)} />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={contentClassName}
        {...props}
      >
        <DialogContentLayoutContext.Provider value={layout}>
          {renderedChildren}
        </DialogContentLayoutContext.Provider>
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => {
  const layout = React.useContext(DialogContentLayoutContext);
  return (
    <div className={getDialogHeaderClassName(className, layout)} {...props} />
  );
};
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => {
  const layout = React.useContext(DialogContentLayoutContext);
  return (
    <div className={getDialogFooterClassName(className, layout)} {...props} />
  );
};
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title> & {
    /** `large` steps up to the 20px display variant; there is no small size. */
    size?: DialogTitleSize;
  }
>(({ className, children, size = "default", ...props }, ref) => (
  <AlertDialogPrimitive.Title ref={ref} asChild>
    <Text
      variant={DIALOG_TITLE_VARIANTS[size]}
      multiline
      className={cn(DIALOG_TITLE_CLASSNAME, className)}
      {...props}
    >
      {children}
    </Text>
  </AlertDialogPrimitive.Title>
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, children, ...props }, ref) => {
  return (
    <AlertDialogPrimitive.Description ref={ref} asChild>
      <Text
        variant="compact-description"
        multiline
        className={cn(DIALOG_DESCRIPTION_CLASSNAME, className)}
        {...props}
      >
        {children}
      </Text>
    </AlertDialogPrimitive.Description>
  );
});
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action ref={ref} className={className} {...props} />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel ref={ref} className={className} {...props} />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
